import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, CheckCircle2, XCircle, AlertCircle, Trophy, Sparkles, GripVertical, Square, CheckSquare, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ScenarioBox, ChoiceOptions, ShortAnswerInput, ShortAnswerReview, QUESTION_TYPE_LABEL, isAnswered } from '../components/QuestionInputs';

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : 0, opacity: isDragging ? 0.8 : 1 };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className={`flex items-center gap-3 p-3 rounded-xl border-2 bg-white text-sm font-medium cursor-grab active:cursor-grabbing transition-shadow ${isDragging ? 'shadow-lg border-[#1B2A4A]' : 'border-stone-200 hover:border-stone-300'}`}>
      <GripVertical className="w-4 h-4 text-stone-400 shrink-0" />
      <span className="flex-1">{children}</span>
    </div>
  );
}

function OrderingQuestion({ question, answer, onChange, questionIdx }) {
  const correctOrder = Array.isArray(question.correct_order) ? question.correct_order : [];
  const items = answer || [...correctOrder].sort(() => Math.random() - 0.5);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Initialize with shuffled order on first render
  useEffect(() => {
    if (!answer && correctOrder.length > 0) {
      const shuffled = [...correctOrder].sort(() => Math.random() - 0.5);
      onChange(shuffled);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      const newOrder = arrayMove(items, oldIndex, newIndex);
      onChange(newOrder);
    }
  };

  if (correctOrder.length === 0) {
    return <p className="text-sm text-stone-500 italic">This ordering question has no steps defined.</p>;
  }

  // Use index-based IDs to handle duplicate step text
  const itemIds = items.map((_, i) => `item-${i}`);

  return (
    <div data-testid={`ordering-question-${questionIdx}`}>
      <p className="text-xs text-stone-500 mb-3 font-medium">Drag items to reorder</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
          const oldIndex = itemIds.indexOf(active.id);
          const newIndex = itemIds.indexOf(over.id);
          onChange(arrayMove(items, oldIndex, newIndex));
        }
      }}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((item, i) => (
              <SortableItem key={itemIds[i]} id={itemIds[i]}>
                <span className="text-stone-400 text-xs mr-2">{i + 1}.</span>
                {item}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function MultiSelectQuestion({ question, answer, onChange, questionIdx }) {
  const selected = answer || [];
  const noneIdx = question.options?.findIndex(o => o.toLowerCase() === 'none of the above');

  const toggleOption = (optIdx) => {
    let newSelected;
    if (optIdx === noneIdx && noneIdx >= 0) {
      // "None of the above" — auto-deselect all others
      newSelected = selected.includes(optIdx) ? [] : [optIdx];
    } else {
      // Regular option — deselect "None of the above" if it was selected
      newSelected = selected.filter(i => i !== noneIdx);
      if (newSelected.includes(optIdx)) {
        newSelected = newSelected.filter(i => i !== optIdx);
      } else {
        newSelected = [...newSelected, optIdx];
      }
    }
    onChange(newSelected);
  };

  return (
    <div className="space-y-3" data-testid={`multiselect-question-${questionIdx}`}>
      <p className="text-xs text-stone-500 font-medium">Select all that apply</p>
      {question.options?.map((option, optIdx) => {
        const isSelected = selected.includes(optIdx);
        const isNone = optIdx === noneIdx && noneIdx >= 0;
        return (
          <button key={optIdx} onClick={() => toggleOption(optIdx)}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all text-sm font-medium ${
              isSelected
                ? isNone ? 'border-amber-500 bg-amber-50 text-stone-900' : 'border-[#1B2A4A] bg-[#1B2A4A]/5 text-stone-900'
                : 'border-stone-200 hover:border-stone-300 text-stone-700 bg-white'
            }`} data-testid={`answer-option-${questionIdx}-${optIdx}`}>
            <div className="flex items-center gap-3">
              {isSelected ? (
                <CheckSquare className={`w-5 h-5 shrink-0 ${isNone ? 'text-amber-500' : 'text-[#1B2A4A]'}`} />
              ) : (
                <Square className="w-5 h-5 text-stone-300 shrink-0" />
              )}
              {option}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function TestPage() {
  const { testId, refresherId } = useParams();
  const isRefresher = !!refresherId;
  const { api } = useAuth();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAllReview, setShowAllReview] = useState(false);

  const fireCelebration = () => {
    const colors = ['#1B2A4A', '#D9480F', '#F59E0B', '#10B981', '#3B82F6'];
    confetti({ particleCount: 120, spread: 100, origin: { y: 0.6 }, colors });
  };

  const loadTest = useCallback(async () => {
    try {
      const { data } = await api('get', isRefresher ? `/refreshers/${refresherId}` : `/tests/${testId}`);
      setTest(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load test');
      navigate('/');
    }
  }, [api, testId, refresherId, isRefresher, navigate]);

  useEffect(() => { loadTest(); }, [loadTest]);

  const selectAnswer = (questionIdx, answerIdx) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [questionIdx]: answerIdx }));
  };

  const submitTest = async () => {
    if (answeredCount < questions.length) {
      toast.error('Please answer all questions before submitting');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api('post', isRefresher ? `/refreshers/${refresherId}/submit` : `/tests/${testId}/submit`, { answers });
      setResult(data);
      setSubmitted(true);
      if (data.passed) {
        toast.success(`You passed! Score: ${data.score}%`);
        setTimeout(fireCelebration, 300);
      } else if (isRefresher) {
        toast.error(`Score: ${data.score}%. Your manager has been notified for a quick re-coach.`);
      } else {
        toast.error(`Score: ${data.score}%. You need ${data.passing_score}% to pass.`);
      }
    } catch {
      toast.error('Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  };

  if (!test) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-[#1B2A4A] border-t-transparent rounded-full" />
      </div>
    );
  }

  const questions = test.questions || [];
  const question = questions[currentQuestion];
  const answeredCount = questions.filter((q, i) => isAnswered(q, answers[i])).length;
  const progressPct = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  const missedQuestions = (result?.review || []).filter(r => !r.is_correct);
  const correctQuestions = (result?.review || []).filter(r => r.is_correct);
  const reviewToShow = showAllReview ? (result?.review || []) : missedQuestions;

  const getQuestionTypeLabel = (type) => QUESTION_TYPE_LABEL[type] || 'Choice';

  return (
    <div className="pb-20 md:pb-6 max-w-2xl mx-auto">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/')}
          className="text-stone-600 hover:text-stone-900 mb-3 -ml-2 rounded-xl" data-testid="back-from-test">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {test.title}
          </h1>
          {isRefresher && <Badge className="bg-[#D9480F]/10 text-[#D9480F] text-xs" data-testid="refresher-badge">{test.interval_days}-day refresher</Badge>}
        </div>
        {isRefresher && !submitted && <p className="text-xs text-stone-500 mt-1">A quick {questions.length}-question check-in to keep this module fresh. One attempt.</p>}
        {!submitted && (
          <div className="flex items-center gap-3 mt-2">
            <Progress value={progressPct} className="h-2 flex-1 bg-stone-100" data-testid="test-progress" />
            <span className="text-sm text-stone-600">{answeredCount}/{questions.length}</span>
          </div>
        )}
      </motion.div>

      {/* ===== RESULT + REVIEW ===== */}
      {submitted && result && (
        <div data-testid="test-results-section">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className={`rounded-2xl border-2 mb-6 ${result.passed ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`} data-testid="test-result">
              <CardContent className="p-6 text-center">
                {result.passed ? <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-2" /> : <XCircle className="w-14 h-14 text-red-500 mx-auto mb-2" />}
                <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Outfit', sans-serif", color: result.passed ? '#16A34A' : '#DC2626' }}>
                  {result.passed ? 'Congratulations!' : 'Not Quite'}
                </h2>
                <p className="text-4xl font-bold text-stone-900 mb-1">{result.score}%</p>
                <p className="text-stone-600 text-sm">{result.correct} out of {result.total} correct — Passing: {result.passing_score}%</p>
                <div className="flex justify-center gap-4 mt-4">
                  <div className="flex items-center gap-1.5 text-sm"><CheckCircle2 className="w-4 h-4 text-green-500" /><span className="font-semibold text-green-700">{correctQuestions.length}</span><span className="text-stone-500">correct</span></div>
                  <div className="flex items-center gap-1.5 text-sm"><XCircle className="w-4 h-4 text-red-500" /><span className="font-semibold text-red-700">{missedQuestions.length}</span><span className="text-stone-500">missed</span></div>
                </div>
                <div className="flex gap-3 justify-center mt-5">
                  <Button onClick={() => navigate('/')} className="bg-stone-200 text-stone-900 rounded-xl hover:bg-stone-300" data-testid="back-to-dash-btn">Back to Dashboard</Button>
                  {!result.passed && !isRefresher && (
                    <Button onClick={() => { setSubmitted(false); setResult(null); setAnswers({}); setCurrentQuestion(0); setShowAllReview(false); }}
                      className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="retry-test-btn">Try Again</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {isRefresher && (
            <Card className={`rounded-2xl border mb-6 ${result.passed ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50'}`} data-testid="refresher-result-card">
              <CardContent className="p-4 text-sm text-stone-700">
                {result.passed ? 'Great retention! Your knowledge of this module is still sharp.' : 'No worries — this is just a refresher. Your manager has been flagged so they can do a quick re-coach with you. The module stays signed off.'}
              </CardContent>
            </Card>
          )}

          {/* Sign-off status */}
          <AnimatePresence>
            {result.passed && result.signoff_status && (
              <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }} data-testid="signoff-pending-card">
                <Card className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-white mb-6">
                  <CardContent className="p-6 text-center">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring', stiffness: 300 }}>
                      {result.signoff_status === 'approved' ? <Trophy className="w-14 h-14 text-[#D9480F] mx-auto mb-3" /> : <Clock className="w-14 h-14 text-amber-500 mx-auto mb-3" />}
                    </motion.div>
                    <h2 className="text-xl font-bold text-[#1B2A4A] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {result.signoff_status === 'approved' ? 'Module already signed off' : 'Sent for Manager Sign-off'}
                    </h2>
                    <p className="text-stone-600 text-sm">
                      {result.signoff_status === 'approved'
                        ? 'This module was previously approved. Great refresher!'
                        : 'Your manager has been notified. Once they review and digitally sign off, your next module will unlock.'}
                    </p>
                    {result.signoff_status === 'pending_signoff' && (
                      <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mt-4">
                        <Sparkles className="w-4 h-4" /> Awaiting sign-off
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Review Section */}
          {(result.review || []).length > 0 && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {showAllReview ? 'All Questions' : `Missed Questions (${missedQuestions.length})`}
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowAllReview(!showAllReview)}
                  className="text-sm text-[#1B2A4A] rounded-xl" data-testid="toggle-review-view">
                  {showAllReview ? 'Show Missed Only' : 'Show All Questions'}
                </Button>
              </div>
              {missedQuestions.length === 0 && !showAllReview && (
                <Card className="bg-green-50 rounded-2xl border border-green-200 mb-4">
                  <CardContent className="p-5 text-center"><CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" /><p className="text-green-800 font-medium">Perfect score!</p></CardContent>
                </Card>
              )}
              <div className="space-y-3" data-testid="review-list">
                {reviewToShow.map((item, i) => (
                  <motion.div key={item.index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}>
                    <Card className={`rounded-2xl border shadow-sm ${item.is_correct ? 'border-green-200' : 'border-red-200'}`} data-testid={`review-card-${item.index}`}>
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex items-start gap-2 mb-3">
                          {item.is_correct ? <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={item.is_correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>Q{item.index + 1}</Badge>
                              <Badge className="bg-stone-100 text-stone-500 text-[10px]">{getQuestionTypeLabel(item.type)}</Badge>
                              {item.partial_credit !== undefined && item.partial_credit > 0 && !item.is_correct && (
                                <Badge className="bg-amber-100 text-amber-700 text-[10px]">{Math.round(item.partial_credit * 100)}% credit</Badge>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.question}</p>
                          </div>
                        </div>
                        {item.type === 'scenario' && <div className="ml-7 mb-2"><ScenarioBox scenario={item.scenario} compact /></div>}
                        {/* Review body by type */}
                        {item.type === 'short_answer' ? (
                          <ShortAnswerReview item={item} />
                        ) : item.type === 'ordering' ? (
                          <div className="ml-7 space-y-1.5">
                            <p className="text-xs text-stone-500 font-medium mb-1">Your order vs correct order:</p>
                            {(item.correct_order || []).map((step, si) => {
                              const userStep = item.user_answer?.[si];
                              const isMatch = userStep === step;
                              return (
                                <div key={si} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${isMatch ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                                  <span className="text-xs font-bold text-stone-400 w-5">{si + 1}.</span>
                                  <span className={isMatch ? 'text-green-900' : 'text-red-900 line-through'}>{userStep}</span>
                                  {!isMatch && <span className="text-green-700 text-xs ml-auto">Correct: {step}</span>}
                                  {isMatch && <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto" />}
                                </div>
                              );
                            })}
                          </div>
                        ) : item.type === 'multi_select' ? (
                          <div className="space-y-2 ml-7">
                            {(item.options || []).map((opt, optIdx) => {
                              const isUserSelected = (item.user_answer || []).includes(optIdx);
                              const isCorrect = (item.correct_answers || []).includes(optIdx);
                              let style = 'border-stone-100 bg-white text-stone-600';
                              if (isCorrect && isUserSelected) style = 'border-green-300 bg-green-50 text-green-900';
                              else if (isCorrect && !isUserSelected) style = 'border-amber-300 bg-amber-50 text-amber-900';
                              else if (!isCorrect && isUserSelected) style = 'border-red-300 bg-red-50 text-red-900 line-through';
                              return (
                                <div key={optIdx} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-sm ${style}`}>
                                  {isCorrect ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /> : isUserSelected ? <XCircle className="w-4 h-4 text-red-500 shrink-0" /> : <div className="w-4 h-4 shrink-0" />}
                                  <span>{opt}</span>
                                  {isCorrect && !isUserSelected && <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-auto">Missed</Badge>}
                                  {!isCorrect && isUserSelected && <Badge className="bg-red-100 text-red-700 text-[10px] ml-auto">Wrong</Badge>}
                                  {isCorrect && isUserSelected && <Badge className="bg-green-100 text-green-700 text-[10px] ml-auto">Correct</Badge>}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="space-y-2 ml-7">
                            {(item.options || []).map((opt, optIdx) => {
                              const isUserAnswer = item.user_answer === optIdx;
                              const isCorrectAnswer = item.correct_answer === optIdx;
                              let style = 'border-stone-100 bg-white text-stone-600';
                              if (isCorrectAnswer) style = 'border-green-300 bg-green-50 text-green-900';
                              else if (isUserAnswer && !item.is_correct) style = 'border-red-300 bg-red-50 text-red-900 line-through';
                              return (
                                <div key={optIdx} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-sm ${style}`} data-testid={`review-option-${item.index}-${optIdx}`}>
                                  {isCorrectAnswer && <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />}
                                  {isUserAnswer && !item.is_correct && <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                                  {!isCorrectAnswer && !isUserAnswer && <div className="w-4 h-4 shrink-0" />}
                                  <span>{opt}</span>
                                  {isCorrectAnswer && <Badge className="bg-green-100 text-green-700 text-[10px] ml-auto shrink-0">Correct</Badge>}
                                  {isUserAnswer && !item.is_correct && <Badge className="bg-red-100 text-red-700 text-[10px] ml-auto shrink-0">Your Answer</Badge>}
                                </div>
                              );
                            })}
                            {item.type === 'scenario' && item.explanation && <p className="text-xs text-stone-600 bg-stone-50 rounded-lg px-3 py-2 mt-1">Why: {item.explanation}</p>}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* ===== QUESTION-TAKING MODE ===== */}
      {!submitted && (
        <>
          <div className="flex gap-1.5 overflow-x-auto pb-3 mb-4 scrollbar-hide" data-testid="question-nav">
            {questions.map((_, i) => (
              <button key={i} onClick={() => setCurrentQuestion(i)}
                className={`w-8 h-8 rounded-full text-xs font-medium shrink-0 transition-all ${
                  i === currentQuestion ? 'bg-[#1B2A4A] text-white'
                  : isAnswered(questions[i], answers[i]) ? 'bg-green-100 text-green-700'
                  : 'bg-stone-100 text-stone-500'
                }`} data-testid={`question-dot-${i}`}>
                {i + 1}
              </button>
            ))}
          </div>

          {question && (
            <motion.div key={currentQuestion} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
              <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm mb-4" data-testid="question-card">
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge className="bg-[#1B2A4A]/10 text-[#1B2A4A]">Question {currentQuestion + 1}</Badge>
                    <span className="text-xs text-stone-400">of {questions.length}</span>
                    <Badge className="bg-stone-100 text-stone-500 text-[10px] ml-auto">{getQuestionTypeLabel(question.type)}</Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {question.question}
                  </h3>

                  {question.type === 'scenario' && <ScenarioBox scenario={question.scenario} />}
                  {/* Render by question type */}
                  {question.type === 'short_answer' ? (
                    <ShortAnswerInput value={answers[currentQuestion]} onChange={(v) => setAnswers(prev => ({ ...prev, [currentQuestion]: v }))} questionIdx={currentQuestion} />
                  ) : question.type === 'scenario' ? (
                    <ChoiceOptions options={question.options} value={answers[currentQuestion]} onChange={(idx) => selectAnswer(currentQuestion, idx)} questionIdx={currentQuestion} />
                  ) : (question.type === 'ordering') ? (
                    <OrderingQuestion
                      question={question}
                      answer={answers[currentQuestion]}
                      onChange={(order) => setAnswers(prev => ({ ...prev, [currentQuestion]: order }))}
                      questionIdx={currentQuestion}
                    />
                  ) : (question.type === 'multi_select') ? (
                    <MultiSelectQuestion
                      question={question}
                      answer={answers[currentQuestion]}
                      onChange={(selected) => setAnswers(prev => ({ ...prev, [currentQuestion]: selected }))}
                      questionIdx={currentQuestion}
                    />
                  ) : (
                    /* Standard multiple_choice */
                    <div className="space-y-3">
                      {question.options?.map((option, optIdx) => (
                        <button key={optIdx} onClick={() => selectAnswer(currentQuestion, optIdx)}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all text-sm font-medium ${
                            answers[currentQuestion] === optIdx
                              ? 'border-[#1B2A4A] bg-[#1B2A4A]/5 text-stone-900'
                              : 'border-stone-200 hover:border-stone-300 text-stone-700 bg-white'
                          }`} data-testid={`answer-option-${currentQuestion}-${optIdx}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                              answers[currentQuestion] === optIdx ? 'border-[#1B2A4A] bg-[#1B2A4A]' : 'border-stone-300'
                            }`}>
                              {answers[currentQuestion] === optIdx && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            {option}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex items-center justify-between gap-3">
                <Button variant="outline" onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                  disabled={currentQuestion === 0} className="rounded-xl border-stone-200" data-testid="prev-question-btn">
                  Previous
                </Button>
                <div className="flex gap-2">
                  {currentQuestion < questions.length - 1 ? (
                    <Button onClick={() => setCurrentQuestion(currentQuestion + 1)}
                      className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="next-question-btn">
                      Next Question
                    </Button>
                  ) : (
                    <Button onClick={submitTest} disabled={submitting || answeredCount < questions.length}
                      className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="submit-test-btn">
                      {submitting ? 'Submitting...' : 'Submit Test'}
                    </Button>
                  )}
                </div>
              </div>
              {answeredCount < questions.length && currentQuestion === questions.length - 1 && (
                <div className="flex items-center gap-2 mt-3 text-sm text-amber-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>Answer all questions to submit ({questions.length - answeredCount} remaining)</span>
                </div>
              )}
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
